import { Box, type BoxProps } from "@mui/material";
import { useUpdateEffect } from "@react-hookz/web";
import { type FC, useCallback, useState } from "react";
import { useSettings } from "../../../utils/hooks/use-settings";
import { useAnalytics } from "../../analytics/analytics";
import { ExpandButton } from "../../generic/components/expand-button/expand-button";

interface AccordionState<T> {
  channel: T;
  details: T;
  to: T;
  when: T;
  message: T;
}

export type OpenState = "open" | "closed" | "confirmed";

export function useAccordionState(): {
  state: AccordionState<OpenState>;
  setOpen: (accordion: keyof AccordionState<OpenState>, open: OpenState) => void;
  transition: (from: keyof AccordionState<OpenState>, to: keyof AccordionState<OpenState>) => void;
  AccordionToggle: FC<BoxProps>;
} {
  const { settings, saveSettings } = useSettings();
  const [state, setState] = useState<AccordionState<OpenState>>({
    channel: "open",
    details: settings.openAccordions ? "open" : "closed",
    to: settings.openAccordions ? "open" : "closed",
    when: settings.openAccordions ? "open" : "closed",
    message: settings.openAccordions ? "open" : "closed",
  });

  useUpdateEffect(
    () =>
      setState({
        channel: settings.openAccordions ? "open" : "closed",
        details: settings.openAccordions ? "open" : "closed",
        to: settings.openAccordions ? "open" : "closed",
        when: settings.openAccordions ? "open" : "closed",
        message: settings.openAccordions ? "open" : "closed",
      }),
    [settings.openAccordions],
  );

  const { gaEvent } = useAnalytics();

  const setOpen = useCallback(
    (accordion: keyof AccordionState<OpenState>, open: OpenState) => {
      setState((oldstate) => ({ ...oldstate, [accordion]: open }));
      gaEvent("accordion", { accordion, open: open === "open", event_category: "moment" });
    },
    [gaEvent],
  );

  const AccordionToggle = useCallback(
    (props: BoxProps) => (
      <AccordionToggleReal
        open={settings.openAccordions}
        setAllOpen={(open: boolean) =>
          saveSettings((oldsettings) => ({ ...oldsettings, openAccordions: open }))
        }
        {...props}
      />
    ),
    [saveSettings, settings.openAccordions],
  );

  const transition = useCallback(
    (from: keyof AccordionState<OpenState>, to: keyof AccordionState<OpenState>) => {
      setState((oldstate) => ({ ...oldstate, [from]: "confirmed", [to]: "open" }));
    },
    [],
  );
  return {
    state,
    setOpen,
    AccordionToggle,
    transition,
  };
}

interface AccordionToggleProps {
  open: boolean;
  setAllOpen(open: boolean): void;
}
const AccordionToggleReal: FC<AccordionToggleProps & BoxProps> = ({
  open,
  setAllOpen,
  sx = [],
  ...props
}) => {
  return (
    <Box
      sx={[
        {
          display: "table",
          ml: "auto",
          mb: 3,
          mt: 3,
        },
        ...(sx instanceof Array ? sx : [sx]),
      ]}
      {...props}
    >
      <ExpandButton
        initialState={open}
        minimizedMessage="Open all accordions"
        expandedMessage="Minimize accordions"
        onClick={setAllOpen}
      />
    </Box>
  );
};
